import { useCallback, useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import dayjs from "dayjs";
import PageHeader, {
  dateRangeState,
} from "../../components/common/pageHeader/PageHeader";
import { ValueCard } from "../../components/common/tiles/ValueCard";
import colorArray, { mediaLineColorArray } from "../../utils/JSON/colorArray";
import Wrapper from "../../components/utils/Wrapper";
import InfoIcon from "../../assets/InfoIcon";
import { tableColumns } from "../../utils/JSON/tableData";
import Table from "../../components/common/charts/TableChart";
import LineChart from "../../components/common/charts/LineChart";
import AreaChart from "../../components/common/charts/AreaChart";
import LoadingComponent from "../../components/common/LoadingComponent";
import SliderComponent from "../../components/common/slider/Slider";
import SectionHeading from "../../components/common/SectionHeading";
import useGraphHook from "../../hooks/useGraphHook";
import useOverviewGraphHook from "../../hooks/useOverviewGraphHook";
import { DateRangeObj } from "../../utils/helperFunctions/useDateFilter";
import getHeading from "../../utils/helperFunctions/getTilesHeadingByMedia";
import getFormatNumber from "../../utils/helperFunctions/getFormatedNumber";

// Interface definitions
interface GraphDataItem {
  pubDate: string;
  totalNoOfArticles: number;
  totalAve: number;
  sentiment_counts: {
    neutral: number;
    positive: number;
    negative: number;
  };
}

interface GraphDataItemResult {
  pubDate: string;
  totalNoOfArticles: number;
  totalAve: number;
  neutral: number;
  positive: number;
  negative: number;
}

interface DigitalDataItem {
  pubDate: string;
  totalAve: number;
  totalNoOfArticles?: number;
  radioNoOfArticles?: number;
  traffic?: number;
  languages: { [key: string]: number };
  sourceNames: { [key: string]: number };
}

export default function Digital() {
  // Recoil State
  const [dateRange, setDateRange] = useRecoilState(dateRangeState);

  // Local State
  const [digitalFilter, setDigitalFilter] = useState<DateRangeObj | undefined>(
    undefined
  );
  const [digitalFilter1, setDigitalFilter1] = useState<
    DateRangeObj | undefined
  >(undefined);
  const [graphData, setGraphData] = useState<GraphDataItemResult[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Custom Hooks
  const {
    graphdata,
    articals,
    cartdata,
    digitalPublicationsdata,
    loading,
    totalInvestment,
  } = useGraphHook(digitalFilter);
  const { graphdata1 } = useOverviewGraphHook(digitalFilter1);

  // Initialize data fetching
  const fetchDigitalData = useCallback(async () => {
    const startDateTime = dateRange?.[0] || dayjs().subtract(1, "month");
    const endDateTime = dateRange?.[1] || dayjs();

    const baseFilter = {
      startDate: startDateTime.toISOString(),
      endDate: endDateTime.toISOString(),
      dateType: "date",
    };

    // Set digital filter
    setDigitalFilter({
      ...baseFilter,
      crawlerType: "digital",
    });

    // Set alltable filter
    setDigitalFilter1({
      ...baseFilter,
      crawlerType: "alltable",
    });
  }, [dateRange, setDigitalFilter, setDigitalFilter1]);

  // Initial data fetch
  useEffect(() => {
    fetchDigitalData();
  }, [fetchDigitalData]);

  // Process graph data
  useEffect(() => {
    const processGraphData = async () => {
      try {
        setIsLoading(true);
        const processedData: GraphDataItemResult[] = graphdata.map(
          (item: GraphDataItem) => ({
            pubDate: item.pubDate,
            totalNoOfArticles: item.totalNoOfArticles,
            totalAve: item.totalAve,
            neutral: item.sentiment_counts?.neutral,
            positive: item.sentiment_counts?.positive,
            negative: item.sentiment_counts?.negative,
          })
        );
        setGraphData(processedData);
      } catch (error) {
        console.error("Error processing graph data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    processGraphData();
  }, [digitalFilter, graphdata]);

  // Handle date range changes
  const handleDateRangeChange = (dateRangeObj: DateRangeObj) => {
    setDigitalFilter(dateRangeObj);
    setDigitalFilter1({
      ...dateRangeObj,
      crawlerType: "alltable",
    });

    // Update Recoil state
    const startDate = dayjs(dateRangeObj.startDate);
    const endDate = dayjs(dateRangeObj.endDate);
    setDateRange([startDate, endDate]);
  };

  // Default data for charts
  const defaultData = [{ x: "2024-01-01", y: 0 }];

  // Transform data for charts
  const overviewTilesChartData = (graphdata as any)?.length
    ? (graphdata as any)?.map((item: DigitalDataItem) => ({
        x: item.pubDate,
        y: item.totalNoOfArticles,
      }))
    : defaultData;

  const overviewTilesChartData1 = (graphdata as any)?.length
    ? (graphdata as any)?.map((item: DigitalDataItem) => ({
        x: item.pubDate,
        y: item.traffic,
      }))
    : defaultData;

  const overviewTilesChartData2 = (graphdata as any)?.length
    ? (graphdata as any)?.map((item: DigitalDataItem) => ({
        x: item.pubDate,
        y: item.totalAve,
      }))
    : defaultData;

  const overviewTilesChartData3 = (graphdata as any)?.length
    ? (graphdata as any)?.map((item: DigitalDataItem) => {
        const totalAve = Number(item.totalAve);
        const roi = ((totalAve - totalInvestment) * 100) / totalInvestment;
        return {
          x: item.pubDate,
          y: roi,
        };
      })
    : defaultData;

  // Media tiles configuration
  const mediaTilesData = [
    {
      chartData: overviewTilesChartData,
      heading: getHeading("digital")?.publication,
      subHeading: "$23.9M",
      description: "total articles ytd",
      customStyle: {
        background: "linear-gradient(72.11deg, #D52800 0%, #FF0000 100%)",
        line: mediaLineColorArray[0],
      },
    },
    {
      chartData: overviewTilesChartData1,
      heading: getHeading("digital")?.traffic,
      subHeading: "$23.9M",
      description: "total traffic ytd",
      customStyle: {
        background: "linear-gradient(72.11deg, #F49600 0%, #FFB338 100%)",
        line: mediaLineColorArray[1],
      },
    },
    {
      chartData: overviewTilesChartData2,
      heading: "Media Value (AVE)",
      subHeading: "$23.9M",
      description: "pr value ytd",
      customStyle: {
        background: "linear-gradient(252.11deg, #3C7BE6 0%, #0048C2 100%)",
        line: mediaLineColorArray[2],
      },
    },
    {
      chartData: overviewTilesChartData3,
      heading: "ROI",
      subHeading: "$23.9M",
      description: "return on investment ytd",
      customStyle: {
        background: "linear-gradient(35.85deg, #129219 0%, #06CD11 100%)",
        line: mediaLineColorArray[3],
      },
    },
  ];

  if (loading) return <LoadingComponent />;

  return (
    <>
      {/* Header Section */}
      <PageHeader
        heading="Digital Media Insights"
        page="digital"
        onDateRangeChange={handleDateRangeChange}
      />

      {/* Value Cards Section */}
      <div className="w-full flex overflow-auto gap-3 mt-3 items-center">
        {mediaTilesData.map((item, index) => (
          <ValueCard
            key={index}
            data={item.chartData}
            title={item.heading}
            // value={
            //   index === 1
            //     ? getFormatNumber(Number(cartdata[index] || 0))
            //     : index
            //       ? cartdata[index] || 0
            //       : parseInt(cartdata[index] || 0)
            // }
            value={cartdata[index] || 0}
            remark={item.description}
            color={item.customStyle}
            hideInsight={true}
          />
        ))}
      </div>

      {/* Digital Publication Trend */}
      <Wrapper customClass="mt-4 !h-[484px]">
        <SectionHeading
          content="Digital Publication Trend"
          customClass="mb-6"
          toolTipText="Tracks the number of published digital articles, revealing trends and peaks in media activity over time."
        />
        {!isLoading && (
          <LineChart
            rawData={{
              data: graphData,
              chartSetting: [
                {
                  dataKey: "totalNoOfArticles",
                  lineColor: colorArray[1],
                  dotColor: colorArray[5],
                },
              ],
              defaultLegend: "totalNoOfArticles",
              showLegend: false,
              unit: "Articles",
            }}
          />
        )}
      </Wrapper>

      {/* Media Value Trend */}
      <Wrapper customClass="mt-4 !h-[484px]">
        <SectionHeading
          content="Digital Media Value (AVE) Trend"
          customClass="mb-6"
          toolTipText="Charts the media value (AVE) of published digital articles, showcasing fluctuations and key trends over time.
"
        />
        {!isLoading && (
          <AreaChart
            rawData={{
              data: graphData,
              chartSetting: [
                {
                  dataKey: "totalAve",
                  fill: colorArray[1],
                },
              ],
            }}
          />
        )}
      </Wrapper>

      {/* Published Articles */}
      <Wrapper
        customClass="mt-4"
        moreInsights={{
          onCLickHandle: () => {
            localStorage.setItem("mediaType", "digital");
          },
          redirect: true,
          path: "/dashboard/content",
        }}
      >
        <SectionHeading
          content="Published Articles"
          customClass="mb-6"
          toolTipText="A visual showcase of recent digital media coverage, highlighting key articles, publications, and timestamps."
        />
        {!isLoading && <SliderComponent cards={articals} mediaType="digital" />}
      </Wrapper>

      {/* Top 10 Digital Publications */}
      <Wrapper customClass="mt-4">
      <SectionHeading
          content="Top 10 Digital Publications"
          customClass="mb-6"
          toolTipText="List of the most influential digita lmedia outlets based on article volume, highlighting their impact on overall news coverage."
        />
        <Table
          columns={tableColumns}
          data={digitalPublicationsdata}
          color={{
            header: "#F99A02",
            headerText: "#F5F6FA",
          }}
          showPagination={false}
        />
      </Wrapper>

      {/* Sentiment Score */}
      <Wrapper customClass="!h-[484px] mt-4">
      <SectionHeading
          content="Sentiment Score"
          customClass="mb-6"
          toolTipText="Sentiment trends in digital news outlets coverage to track public perception over time."
        />
        <LineChart
          rawData={{
            data: graphData,
            chartSetting: [
              {
                dataKey: "positive",
                label: "positive",
                lineColor: colorArray[3],
                dotColor: colorArray[4],
              },
              {
                dataKey: "neutral",
                label: "neutral",
                lineColor: colorArray[1],
                dotColor: colorArray[4],
              },
              {
                dataKey: "negative",
                label: "negative",
                lineColor: colorArray[0],
                dotColor: colorArray[1],
              },
            ],
            defaultLegend: "positive",
            unit: "Sentiment",
            customeStyle: {
              legendStyle: {
                radioGroupSpace: {
                  gap: "15%",
                  justifyContent: "center",
                },
              },
            },
          }}
        />
      </Wrapper>
    </>
  );
}
